import { type LucideIcon } from 'lucide-react';
import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/utils';

/*
 * Surfaces et signaux d'état.
 *
 * Le vocabulaire visuel est délibérément restreint : une carte, un panneau, une
 * pastille d'état. Multiplier les conteneurs produit exactement l'impression de
 * démonstration que le §19 demande d'éviter.
 */

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  padded?: boolean;
}

export function Card({ padded = true, className, children, ...props }: CardProps): React.JSX.Element {
  return (
    <div
      className={cn(
        'rounded-lg border border-line-subtle bg-raised shadow-[var(--shadow-card)]',
        padded && 'p-5',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export interface SectionHeaderProps {
  title: string;
  description?: string;
  actions?: ReactNode;
  className?: string;
}

export function SectionHeader({
  title,
  description,
  actions,
  className,
}: SectionHeaderProps): React.JSX.Element {
  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      <div className="min-w-0">
        <h2 className="text-sm font-semibold tracking-tight text-ink">{title}</h2>
        {description && <p className="mt-0.5 text-xs text-ink-faint">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

export type Tone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info';

const TONE_STYLES: Record<Tone, string> = {
  neutral: 'bg-neutral-soft text-ink-muted border-neutral-border',
  accent: 'bg-accent-soft text-accent-text border-accent-border',
  success: 'bg-success-soft text-success border-success-border',
  warning: 'bg-warning-soft text-warning border-warning-border',
  danger: 'bg-danger-soft text-danger border-danger-border',
  info: 'bg-info-soft text-info border-info-border',
};

const DOT_STYLES: Record<Tone, string> = {
  neutral: 'bg-ink-faint',
  accent: 'bg-accent',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  info: 'bg-info',
};

export interface BadgeProps {
  tone?: Tone;
  dot?: boolean;
  className?: string;
  children: ReactNode;
}

export function Badge({
  tone = 'neutral',
  dot = false,
  className,
  children,
}: BadgeProps): React.JSX.Element {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5',
        'text-2xs font-medium whitespace-nowrap',
        TONE_STYLES[tone],
        className,
      )}
    >
      {dot && <span className={cn('size-1.5 rounded-full', DOT_STYLES[tone])} aria-hidden />}
      {children}
    </span>
  );
}

export interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps): React.JSX.Element {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-14 text-center', className)}>
      {Icon && (
        <div className="mb-3 flex size-11 items-center justify-center rounded-full bg-sunken text-ink-faint">
          <Icon className="size-5" aria-hidden />
        </div>
      )}
      <p className="text-sm font-medium text-ink">{title}</p>
      {description && <p className="mt-1 max-w-sm text-xs text-ink-faint">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }): React.JSX.Element {
  return <div className={cn('skeleton rounded-md', className)} aria-hidden />;
}

/** Placeholder de tableau : reproduit la densité réelle pour éviter le saut de mise en page. */
export function TableSkeleton({ rows = 6, columns = 5 }: { rows?: number; columns?: number }): React.JSX.Element {
  return (
    <div className="divide-y divide-line-subtle">
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-4 px-4 py-3">
          {Array.from({ length: columns }, (_, columnIndex) => (
            <Skeleton
              key={columnIndex}
              className={cn('h-3.5', columnIndex === 0 ? 'w-32' : 'flex-1')}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export interface StatCardProps {
  label: string;
  value: string;
  icon?: LucideIcon;
  hint?: string;
  changeRatio?: number | null;
  /** Certains indicateurs s'améliorent en baissant (erreurs, doublons). */
  invertChange?: boolean;
  tone?: Tone;
}

export function StatCard({
  label,
  value,
  icon: Icon,
  hint,
  changeRatio,
  invertChange = false,
  tone = 'neutral',
}: StatCardProps): React.JSX.Element {
  const hasChange = changeRatio !== null && changeRatio !== undefined && Number.isFinite(changeRatio);
  const improving = hasChange ? (invertChange ? changeRatio < 0 : changeRatio > 0) : false;
  const flat = hasChange && Math.abs(changeRatio) < 0.005;

  return (
    <Card className="flex flex-col justify-between gap-3">
      <div className="flex items-start justify-between gap-3">
        <span className="text-xs font-medium text-ink-muted">{label}</span>
        {Icon && (
          <span
            className={cn(
              'flex size-7 items-center justify-center rounded-md border',
              TONE_STYLES[tone],
            )}
            aria-hidden
          >
            <Icon className="size-3.5" />
          </span>
        )}
      </div>

      <div className="flex items-end justify-between gap-3">
        <span className="tabular text-2xl leading-none font-semibold tracking-tight text-ink">
          {value}
        </span>

        {hasChange && !flat && (
          <span
            className={cn(
              'tabular text-2xs font-medium',
              improving ? 'text-success' : 'text-danger',
            )}
            title="Variation par rapport à la période précédente"
          >
            {changeRatio > 0 ? '+' : ''}
            {(changeRatio * 100).toFixed(0)} %
          </span>
        )}
      </div>

      {hint && <p className="text-2xs text-ink-faint">{hint}</p>}
    </Card>
  );
}

/** Barre de progression sobre, utilisée pour l'avancement d'un lot. */
export function ProgressBar({
  value,
  tone = 'accent',
  className,
}: {
  value: number;
  tone?: Tone;
  className?: string;
}): React.JSX.Element {
  const clamped = Math.max(0, Math.min(100, value));

  return (
    <div
      className={cn('h-1.5 w-full overflow-hidden rounded-full bg-sunken', className)}
      role="progressbar"
      aria-valuenow={Math.round(clamped)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className={cn('h-full rounded-full transition-[width] duration-500', DOT_STYLES[tone])}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}
